import { useContext } from "react";
import { QueryClient, QueryClientContext, useMutation, useQuery } from "@tanstack/react-query";
import { api, type Proficiency, type UserPreferences } from "@/lib/api";

/**
 * Ask Lilia's level, saved on the account (the user, 12 Oct: ask once, save it, skipping
 * means Simple; Olivia's handoff-ask-lilia-level). `level` is null until the author answers;
 * `known` is false while it loads, or where there is no query client (some isolated views),
 * so nothing asks before we know.
 *
 * Shares the ["preferences"] cache with usePreferences.
 */
const FALLBACK = new QueryClient();

export const ASK_LEVELS: Proficiency[] = ["beginner", "intermediate", "advanced"];

/** `signedIn` false skips the account (help read signed out): the level is then Simple. */
export function useAskLevel(signedIn = true) {
  const context = useContext(QueryClientContext);
  const client = context ?? FALLBACK;
  const query = useQuery(
    {
      queryKey: ["preferences"],
      queryFn: async (): Promise<UserPreferences> => (await api.preferences.get()).preferences,
      enabled: !!context && signedIn,
      staleTime: 60_000,
    },
    client,
  );
  const mutation = useMutation(
    {
      mutationFn: async (askLevel: Proficiency) => (await api.preferences.update({ askLevel })).preferences,
      onMutate: (askLevel) => {
        client.setQueryData<UserPreferences>(["preferences"], (p) => (p ? { ...p, askLevel } : p));
      },
      onSuccess: (prefs) => client.setQueryData(["preferences"], prefs),
    },
    client,
  );
  const saved = query.data?.askLevel;
  return {
    /** The saved level, or null when the author hasn't answered yet. */
    level: saved && ASK_LEVELS.includes(saved) ? saved : null,
    /** What Ask Lilia uses: the saved level, else Simple. */
    effective: (saved && ASK_LEVELS.includes(saved) ? saved : "beginner") as Proficiency,
    known: !!context && query.isSuccess,
    setLevel: (l: Proficiency) => mutation.mutate(l),
  };
}
